<template>
    <div v-if="!processedThreatsData" class="q-pa-sm">No coastal threats data available for this area.</div>
    <div v-else ref="root" id="CoastalThreatsPane" class="threats-pane q-pa-sm" v-bind="attrs">
        <div class="text-weight-medium text-secondary q-pl-sm q-pt-sm">
            COASTAL CHANGE LIKELIHOOD
            <q-btn dense size="xs" flat class="gray q-mb-sm" icon="help_outline" @click="infoDialogs['ccl'].model = !infoDialogs['ccl'].model" aria-label="Diadromous fish data help"/>
        </div>
        <div class="q-pa-sm">
            <div class="row" v-if="processedThreatsData.ccl3AllAcres">
                <div class="col-7">
                    Coastal Habitats LIkely to Change
                </div>
                <div class="col-2 text-right text-bold">{{ processedThreatsData.ccl3AllAcres }} ac</div>
            </div>
            <q-expansion-item dense group="threats"  header-class="bg-grey-3" label="Coastal Change Details" class="expander q-mt-sm">
                <div v-if="cclStackedHabitatData && cclStackedHabitatData.length" class="q-pl-lg">
                    <div v-for="(habData, i) in cclStackedHabitatData" :key="habData[0]?.habitat" class="q-mb-none">
                        <div class="text-caption text-bold q-mb-xs">{{ habData[0]?.habitat }}</div>
                        <StackedBar
                            :data="habData"
                            :divName="`ccl-habitat-stack-${i}`"
                            class="q-mt-none" 
                            categoryProp="class"
                            barName="habitat"
                            valueProp="percent"
                            :sortCategories="cclLikelihoodOrder"
                            :categoryColors="cclLikelihoodColors"
                            :barWidth="700"
                            :labelFontSize="0"
                            :xAxisTicks="false"
                            :yAxisTicks="false"
                            :topMargin="5"
                            :leftMargin="30"
                            :rectOutline="true" 
                        />
                    </div>
                    <div class="text-caption q-pr-sm" v-for="item in cclLegendItems" :key="item.attr">
                        <div class="row items-center justify-between">
                            <div class="row items-center no-wrap">
                                <span class="legend-swatch" :style="{ backgroundColor: cclLikelihoodColors[item.label] }" />&nbsp
                                <span>{{ item.label }}</span>
                            </div>
                            <div class="col-1 items-center self-center bordered q-mb-xs pointer" @click.stop="styleThreatsMapAttr(item.attr, item.label, cclLikelihoodColors[item.label])" :class="activeMapAttr(item.attr) ? 'mapActive' : ''">
                                <q-icon size="xs" v-if="!activeMapAttr(item.attr)" name="chevron_right" />
                                <q-icon color="white" v-else name="star" />
                                <q-tooltip>View this data in the map</q-tooltip>
                            </div>
                        </div>
                    </div>

                </div>
            </q-expansion-item>


        </div>
        <q-separator />
        <div class="text-weight-medium text-secondary q-pl-sm q-pt-sm">
            HAZARD IMPACT TYPE
            <q-btn dense size="xs" flat class="gray q-mb-sm" icon="help_outline" @click="infoDialogs['hazard'].model = !infoDialogs['hazard'].model" aria-label="Diadromous fish data help"/>
        </div>
        <div class="q-pa-sm">
            <div class="row" v-if="processedThreatsData.hazClass4Total">

                <div class="text-caption">The information below highlights the hazard type that may be most influential in coastal change for a location. A perpetual hazard is continually acting on the landscape (e.g., relative sea level rise, coastal erosion, storm frequency) while an event hazard (e.g., wave hazard, overwash, tidal flooding) occurs intermittently, but often with significant impact</div>
                <div class="col-8 q-pt-sm">
                    High likelihood of hazard (perpetual or event)
                </div>
                <div class="col-2 q-pt-sm text-right text-bold">{{ processedThreatsData.hazClass4Total }} ac</div>
            </div>
            <q-expansion-item dense group="threats"  header-class="bg-grey-3"  label="Hazard Class Details" class="expander q-mt-sm ">
                <div v-if="hazStackedHabitatData && hazStackedHabitatData.length"  class="q-pl-lg">
                    <div v-for="(habData, i) in hazStackedHabitatData" :key="habData[0]?.habitat" class="q-mb-none">
                        <div class="text-caption text-bold q-mb-xs">{{ habData[0]?.habitat }}</div>
                        <StackedBar
                            :data="habData"
                            :divName="`haz-habitat-stack-${i}`"
                            class="q-mt-none"
                            categoryProp="class"
                            barName="habitat"
                            valueProp="percent"
                            :sortCategories="hazLikelihoodOrder"
                            :categoryColors="hazLikelihoodColors"
                            :barWidth="700"
                            :labelFontSize="0"
                            :xAxisTicks="false"
                            :yAxisTicks="false"
                            :topMargin="5"
                            :leftMargin="30"
                            :rectOutline="true"
                        />
                    </div>
                    <div class="text-caption  q-pr-sm" v-for="item in hazLegendItems" :key="item.attr">
                        <div class="row items-center justify-between">
                            <div class="row items-center no-wrap">
                                <span class="legend-swatch" :style="{ backgroundColor: hazLikelihoodColors[item.label] }" />&nbsp
                                <span>{{ item.label }}</span>
                            </div>
                            <div class="col-1 items-center self-center bordered q-mb-xs pointer" @click.stop="styleThreatsMapAttr(item.attr, item.label, hazLikelihoodColors[item.label])" :class="activeMapAttr(item.attr) ? 'mapActive' : ''">
                                <q-icon size="xs" v-if="!activeMapAttr(item.attr)" name="chevron_right" />
                                <q-icon color="white" v-else name="star" />
                                <q-tooltip>View this data in the map</q-tooltip>
                            </div>
                        </div>
                    </div>
                </div>
            </q-expansion-item>
        </div>
    </div>

    <div v-for="(infoObj, name) in infoDialogs" :key="name" >
        <q-dialog v-model="infoObj.model">
            <InfoDialog  :html="infoObj.text" :name="infoObj.pName"/>
        </q-dialog>
    </div>
</template>
<script setup lang="ts">
// @ts-nocheck
import { toRefs, ref, computed, onMounted, useAttrs } from "vue";
import { useMapItems } from "../composables/useMapItems"
import InfoDialog from "./InfoDialog.vue";
import StackedBar from "./StackedBar.vue";




const attrs = useAttrs()
const {  tileLayers, toggleLayer, print, printing, ecos, styleMap, clearMapStyle, setLegend, mapStyleInfo, setProcessedData, processedData, loadDataByID, activeLayerID, showRestorationProjectPoint, clearRestorationProjectPoint } = useMapItems()

const props = defineProps({
    data: { type: Object, required: true },
    version: { type: Number, required: true },
})
const { data, version } = toRefs(props)

type ThreatsCellValue = number | string | null | undefined
type ThreatsCell = Record<string, ThreatsCellValue>

const threatsData = computed<ThreatsCell[] | null>(() => {
    const raw = (data.value as Record<string, unknown>)?.['COASTAL_THREATS']
    if (!Array.isArray(raw) || raw.length === 0) return null

    const filtered = (raw as ThreatsCell[]).filter((cell) =>
        Object.entries(cell).some(([k, v]) =>
            (k.toLowerCase().startsWith('haz') || k.toLowerCase().startsWith('ccl')) && Number(v) > 0
        )
    )
    return filtered.length > 0 ? filtered : null
})

type ThreatsSummaryRow = { label: string; value: number }

const processedThreatsData = computed<{ ccl: ThreatsSummaryRow[]; haz: ThreatsSummaryRow[]; ccl3AllAcres?: number; hazClass4Total?: number } | null>(() => {
    const versionKey = version.value
    const processedDataByVersion = processedData.value as Record<number, Record<string, any>>

    if (
        !processedDataByVersion[versionKey] ||
        processedDataByVersion[versionKey]['COASTAL_THREATS_PROCESSED'] === undefined
    ) {
        const cells = threatsData.value
        if (!cells) return null

        const allKeys = new Set<string>()
        cells.forEach((cell) => Object.keys(cell).forEach((key) => allKeys.add(key)))

        const cclFields = Array.from(allKeys).filter((k) => k.toLowerCase().startsWith('ccl'))
        const hazFields = Array.from(allKeys).filter((k) => k.toLowerCase().startsWith('haz'))

        const sumField = (field: string) =>
            cells.reduce((acc, cell) => acc + (Number(cell[field]) || 0), 0)

        const sumCcl3AllSqKm = cells.reduce((acc, cell) => {
            return acc + Object.entries(cell).reduce((fieldAcc, [key, value]) => {
                return key.toLowerCase() === 'ccl3_all' ? fieldAcc + (Number(value) || 0) : fieldAcc
            }, 0)
        }, 0)

        const ccl3AllAcres = Math.round(sumCcl3AllSqKm * 247.105381 * 100) / 100

        const sumHaz4TotalSqKm = hazFields
            .filter((field) => field.toLowerCase().startsWith('haz4'))
            .reduce((acc, field) => acc + sumField(field), 0)

        const hazClass4Total = Math.round(sumHaz4TotalSqKm * 247.105381 * 100) / 100

        // parse "CCL2_Marsh" → class "2", habitat "Marsh"
        const toCCLRow = (field: string): ThreatsSummaryRow => {
            const [prefix, ...rest] = field.split('_')
            const cls = prefix.replace(/^CCL/i, '')
            const habitat = rest.join(' ')
            const label = `${habitat} ${cclLikelihoodLabels[cls] ?? cls}`
            return { label, value: Math.round(sumField(field) * 10000) / 10000 }
        }

        // parse "Haz3_Unconsolidated_Shore" → class "3", habitat "Unconsolidated Shore"
        const toHazRow = (field: string): ThreatsSummaryRow => {
            const [prefix, ...rest] = field.split('_')
            const cls = prefix.replace(/^Haz/i, '')
            const habitat = rest.join(' ')
            const label = `${habitat} ${hazClassLabels[cls] ?? cls}`
            return { label, value: Math.round(sumField(field) * 10000) / 10000 }
        }

        const results = {
            ccl: cclFields.map(toCCLRow).filter((r) => r.value > 0),
            haz: hazFields.map(toHazRow).filter((r) => r.value > 0),
            ccl3AllAcres: ccl3AllAcres > 0 ? ccl3AllAcres : undefined,
            hazClass4Total: hazClass4Total > 0 ? hazClass4Total : undefined,
        }

        ;(setProcessedData as (...args: any[]) => void)(versionKey, 'COASTAL_THREATS_PROCESSED', null, null, results)
        return results
    } else {
        return processedDataByVersion[versionKey]['COASTAL_THREATS_PROCESSED']
    }
})

const cclLikelihoodLabels: Record<string, string> = {
    '1': 'Unlikely to Change',
    '2': 'Uncertain',
    '3': 'Likely to Change',
}

const cclLikelihoodOrder = [
    cclLikelihoodLabels['1'],
    cclLikelihoodLabels['2'],
    cclLikelihoodLabels['3'],
]

const cclLikelihoodColors = {
    [cclLikelihoodLabels['3']]: '#EEAB5B',
    [cclLikelihoodLabels['2']]: '#EBEBEB',
    [cclLikelihoodLabels['1']]: '#6334BA',
}

const hazClassLabels: Record<string, string> = {
    '1': 'Low likelihood of any hazard',
    '2': 'High likelihood of event-type hazards only',
    '3': 'High likelihood of perpetual-type hazards only',
    '4': 'High likelihood of both types of hazards',
}

const hazLikelihoodOrder = [
    hazClassLabels['1'],
    hazClassLabels['2'],
    hazClassLabels['3'],
    hazClassLabels['4']    
]

const hazLikelihoodColors = {
    [hazClassLabels['4']]: '#815929',
    [hazClassLabels['3']]: '#E2973D',
    [hazClassLabels['2']]: '#EEDF5B',
    [hazClassLabels['1']]: '#EBEBEB',
}

const cclLegendItems = [
    { label: cclLikelihoodLabels['1'], attr: 'CCL1_all' },
    { label: cclLikelihoodLabels['2'], attr: 'CCL2_all' },
    { label: cclLikelihoodLabels['3'], attr: 'CCL3_all' },
]

const hazLegendItems = [
    { label: hazClassLabels['1'], attr: 'HAZ1_all' },
    { label: hazClassLabels['2'], attr: 'HAZ2_all' },
    { label: hazClassLabels['3'], attr: 'HAZ3_all' },
    { label: hazClassLabels['4'], attr: 'HAZ4_all' },
]
    const legendColors = {
        '#EEAB5B': [
            '#e6830e',
            '#e39840',
            '#dba86b',
            '#e8c499',
            '#f2e3d0',
        ],
        '#6334BA': [
            '#540fd4',
            '#6334BA',
            '#7a57ba',
            '#b398e3',
            '#cdbee8',
        ],
        "#815929" :[
            "#78460a",
            "#815929",
            "#a17a4c",
            "#ab9378",
            "#bdafa0"
        ],
        "#E2973D" : [
            "#f28d13",    
            "#E2973D",
            "#e6af6e",
            "#e6c297",
            "#e8d5be"
        ],
        "#EEDF5B" : [
            "#e6d119",
            "#EEDF5B",
            "#eae57f",
            "#ebeaa3",
            "#ece1c8"
        ],
        "#EBEBEB" : [
            "#2e2e2e",
            "#5c5c5c",
            "#8f8f8f",
            "#bfbfbf",
            "#EBEBEB"
        ]
    }
const getAllAttributeExpression = (attr: string, baseColor: string) => {

    const colors = legendColors[baseColor] ?? [
        '#2171b5',
        '#6baed6',
        '#9ecae1',
        '#c6dbef',
        '#eff3ff',
    ]

    return [
        'interpolate',
        ['linear'],
        ['coalesce', ['get', attr], 0],
        0, 'rgba(255,255,255,0)',
        0.0001, colors[4],
        0.5, colors[3],
        1, colors[2],
        2, colors[1],
        2.5, colors[0],
    ]
}

const styleThreatsMapAttr = (attr: string, label: string, baseColor: string) => {
    if (!activeLayerID.value) return

    const colors = legendColors[baseColor] ?? [
        '#2171b5',
        '#6baed6',
        '#9ecae1',
        '#c6dbef',
        '#eff3ff',
    ]

    ;(clearMapStyle as (...args: any[]) => void)(activeLayerID.value)
    ;(styleMap as (...args: any[]) => void)(activeLayerID.value, attr, 'expression', getAllAttributeExpression(attr, baseColor))
    ;(setLegend as (...args: any[]) => void)(`${label}`, [
        ['> 2.5 sq km', colors[0]],
        ['2 sq km', colors[1]],
        ['1 sq km', colors[2]],
        ['0.5 sq km', colors[3]],
        ['> 0 sq km', colors[4]],
    ])
}



const activeMapAttr = (attr: string) => {
    const current = mapStyleInfo.value as { attr?: string } | undefined
    return current?.attr === attr
}

const cclStackedHabitatData = computed(() => {
    const cells = threatsData.value
    if (!cells || cells.length === 0) return null

    const habitatTotals = new Map<string, Record<string, number>>()

    for (const cell of cells) {
        for (const field of Object.keys(cell)) {
            if (!field.toLowerCase().startsWith('ccl')) continue
            const [prefix, ...rest] = field.split('_')
            const cls = prefix.replace(/^CCL/i, '')
            const habitat = rest.join(' ')
            if (!habitat || habitat.toLowerCase() === 'all') continue
            const value = Number(cell[field]) || 0
            if (value <= 0) continue

            const totals = habitatTotals.get(habitat) ?? { total: 0, '1': 0, '2': 0, '3': 0 }
            totals.total += value
            totals[cls] = (totals[cls] || 0) + value
            habitatTotals.set(habitat, totals)
        }
    }

    const habitatRows: Array<Array<{ habitat: string; class: string; percent: number }>> = []
    habitatTotals.forEach((totals, habitat) => {
        if (!totals.total) return
        habitatRows.push([
            {
                habitat,
                class: cclLikelihoodLabels['3'],
                percent: Math.round(((totals['3'] || 0) / totals.total) * 10000) / 100,
            },
            {
                habitat,
                class: cclLikelihoodLabels['2'],
                percent: Math.round(((totals['2'] || 0) / totals.total) * 10000) / 100,
            },
            {
                habitat,
                class: cclLikelihoodLabels['1'],
                percent: Math.round(((totals['1'] || 0) / totals.total) * 10000) / 100,
            },
        ])
    })

    return habitatRows.length > 0 ? habitatRows : null
})

const hazStackedHabitatData = computed(() => {
    const cells = threatsData.value
    if (!cells || cells.length === 0) return null

    const habitatTotals = new Map<string, Record<string, number>>()

    for (const cell of cells) {
        for (const field of Object.keys(cell)) {
            if (!field.toLowerCase().startsWith('haz')) continue
            const [prefix, ...rest] = field.split('_')
            const cls = prefix.replace(/^Haz/i, '')
            const habitat = rest.join(' ')
            if (!habitat || habitat.toLowerCase() === 'all') continue
            const value = Number(cell[field]) || 0
            if (value <= 0) continue

            const totals = habitatTotals.get(habitat) ?? { total: 0, '1': 0, '2': 0, '3': 0, '4': 0 }
            totals.total += value
            totals[cls] = (totals[cls] || 0) + value
            habitatTotals.set(habitat, totals)
        }
    }

    const habitatRows: Array<Array<{ habitat: string; class: string; percent: number }>> = []
    habitatTotals.forEach((totals, habitat) => {
        if (!totals.total) return
        habitatRows.push([
            {
                habitat,
                class: hazClassLabels['4'],
                percent: Math.round(((totals['4'] || 0) / totals.total) * 10000) / 100,
            },
            {
                habitat,
                class: hazClassLabels['3'],
                percent: Math.round(((totals['3'] || 0) / totals.total) * 10000) / 100,
            },
            {
                habitat,
                class: hazClassLabels['2'],
                percent: Math.round(((totals['2'] || 0) / totals.total) * 10000) / 100,
            },
            {
                habitat,
                class: hazClassLabels['1'],
                percent: Math.round(((totals['1'] || 0) / totals.total) * 10000) / 100,
            },
        ])
    })

    return habitatRows.length > 0 ? habitatRows : null
})

const publicBaseUrl = import.meta.env.BASE_URL || '/'
const hazardGraphicPath = `${publicBaseUrl}hazardGraphic.png`

const infoDialogs = ref({
    wetlands: {
        pName: "Coastal Marshes",
        model: false,
        text: "More info about the importance of coastal marshes."
    },
    hazard:{
        pName: "Hazard Impact type",
        model: false,
        text: `<div>As part of the USGS Coastal Change Likelihood (CCL) analysis, the Hazard Impact Type was developed to highlight the hazard type (event or perpetual) that may be most influential in coastal change for a location. A perpetual hazard is continually acting on the landscape (e.g., relative sea level rise, coastal erosion, storm frequency) while an event hazard (e.g., wave hazard, overwash, tidal flooding) occurs intermittently, but often with significant impact. </div><div class='q-pt-sm'>Some locations have a high likelihood of both a perpetual and event hazard and others have a dominant hazard type such as high event and low perpetual. Using the USGS Hazard Impact Type classes (image below), we summarized the area and calculated the percentage of each of the four classes for the 2-km grid cells in our tool. We display the percentage results for all the classes except for the low perpetual and low event (dark blue box in the image below). For more information about the hazards synthesized in the CCL analysis, visit <a href='https://geonarrative.usgs.gov/ccl/' target='_blank'>https://geonarrative.usgs.gov/ccl/</a> to read the report, download the data, and view a story map that summarizes the analysis details. <div><img src="${hazardGraphicPath}"/></div></div>`
    },
    ccl:{
        pName: "Coastal Change Liklihood (CCL)",
        model: false,
        text: "<div>The USGS Coastal Change Likelihood (CCL) assessment synthesized over twenty coastal data sources that describe the landscape and the hazards that act on it. The landscape and hazard datasets were combined using supervised machine learning to determine the likelihood of change on the coast in the next decade. The outcomes are landscape specific and are described in relative terms of higher and lower likelihood of coastal change. The CCL values range from 1 to 10 with 1 indicating the location is extremely unlikely to change and 10 indicating the area is extremely likely to change. For use in our tool, we classified the CCL values into three classes as follows:</div><ul><li>Value 1-3: Change unlikely</li><li>Values 4-7: Change uncertain</li><li>Values 8 -10: Change likely </li></ul><div class='q-pt-sm'>We summarized the area and calculated the percentage of each of the three classes by the eight landscape types for the 2-km grid cells in our tool. For more information about the CCL analysis, visit <a href='https://geonarrative.usgs.gov/ccl/' target='_blank'>https://geonarrative.usgs.gov/ccl/</a> to read the report, download the data, and view a story map that summarizes the analysis details. </div>"
    }
})


onMounted(() => {
    if (activeLayerID.value != 'COASTAL_THREATS') {
        loadDataByID('COASTAL_THREATS', version.value)
    }
})


</script>
<style scoped>
.row-title{
    background-color: #cacaca;
}
.hazards-pane{
    max-width: 500px;
}
.details-header{
    background-color: #1D8177;
}
.details-card{
    border: 1px solid grey;
}
.legend-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
}
.legend-item {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    color: #424242;
}
.legend-swatch {
    width: 0.75rem;
    height: 0.75rem;
    display: inline-block;
    border-radius: 2px;
    border: 1px solid rgba(0,0,0,0.1);
}
.bordered{
    border: 1px solid black;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 18px;
    line-height: 1;
    border-radius: 3px;
}
.mapActive{
    background-color: #1D8177;
}
.expander{
    border: 1px solid #989999;
}
</style>